import React, { useState } from 'react';
import { Calendar, Flame, Compass, CheckCircle, XCircle, ChevronDown, ChevronUp, List, Star, Eye } from 'lucide-react';

/**
 * What each primary filter is called, in the app's own words.
 *
 * Exported so the list view can title itself with the filter that produced it
 * rather than a hardcoded heading. The list said "All Leads" regardless — wrong
 * twice over: it holds customers as well as leads, and it said so even when the
 * active filter was AI Recommendations or Today's Route.
 */
export const FILTER_LABELS = {
  planned_today: "Today's Route",
  urgent: 'Urgent Attention',
  ai_picks: 'AI Recommendations',
  new_opportunities: 'New Opportunities',
  all_entities: 'All Entities',
  done: 'Recently Completed',
  skipped: 'Skipped / Cancelled',
};

/**
 * SimpleFilters - Three-Tier Filtering for Salespeople
 *
 * Primary: Today's Route, Urgent Attention, AI Recommendations, New Opportunities, All Entities
 * Review: Recently Completed, Skipped/Cancelled
 * Secondary: All / Leads Only / Customers Only
 */
const SimpleFilters = ({ onFilterChange, currentFilters = {} }) => {
  const [primaryFilter, setPrimaryFilter] = useState(currentFilters.primary || 'ai_picks');
  const [entityType, setEntityType] = useState(currentFilters.entityType || null);
  const [showRefine, setShowRefine] = useState(false);
  const [showReview, setShowReview] = useState(false);

  // Primary filters — main action filters
  const primaryFilters = [
    {
      key: 'planned_today',
      label: "Today's Route",
      icon: Calendar,
      description: 'My visits for today'
    },
    {
      key: 'urgent',
      label: 'Urgent Attention',
      icon: Flame,
      description: 'At-risk customers & overdue visits'
    },
    {
      key: 'ai_picks',
      label: 'AI Recommendations',
      icon: Compass,
      description: 'AI-recommended visits'
    },
    {
      key: 'new_opportunities',
      label: 'New Opportunities',
      icon: Star,
      description: 'New leads to explore'
    },
    {
      key: 'all_entities',
      label: 'All Entities',
      icon: List,
      description: 'See everything'
    }
  ];

  // Review filters — retrospective
  const reviewFilters = [
    {
      key: 'done',
      label: 'Recently Completed',
      icon: CheckCircle,
      description: 'Completed visits'
    },
    {
      key: 'skipped',
      label: 'Skipped / Cancelled',
      icon: XCircle,
      description: 'Visits skipped or cancelled'
    }
  ];

  // Handle primary filter change
  const handlePrimaryChange = (filterKey) => {
    setPrimaryFilter(filterKey);
    notifyParent(filterKey, entityType);
  };

  // Handle entity type change
  const handleEntityTypeChange = (type) => {
    setEntityType(type);
    notifyParent(primaryFilter, type);
  };

  // Notify parent component of filter changes
  const notifyParent = (primary, entity) => {
    onFilterChange({
      primary,
      entityType: entity
    });
  };

  // Get badge color based on filter
  const getBadgeColor = (filterKey) => {
    const colors = {
      planned_today: 'bg-blue-500',
      urgent: 'bg-red-500',
      ai_picks: 'bg-emerald-500',
      new_opportunities: 'bg-purple-500',
      all_entities: 'bg-gray-600',
      done: 'bg-green-500',
      skipped: 'bg-gray-500'
    };
    return colors[filterKey] || 'bg-gray-500';
  };

  return (
    <div className="p-4 border-t border-gray-700">
      <div className="text-xs font-semibold text-gray-400 mb-4 uppercase tracking-wider">
        Quick Filters
      </div>

      {/* Primary Filters — Always Visible */}
      <div className="space-y-2 mb-4">
        {primaryFilters.map((filter) => {
          const Icon = filter.icon;
          const isActive = primaryFilter === filter.key;

          return (
            <button
              key={filter.key}
              onClick={() => handlePrimaryChange(filter.key)}
              className={`
                w-full px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-all
                ${isActive
                  ? `${getBadgeColor(filter.key)} text-white shadow-lg transform scale-[1.02]`
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-gray-50'
                }
              `}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium flex-1 text-left">{filter.label}</span>
            </button>
          );
        })}
      </div>

      {/* Review Section — Collapsible */}
      <button
        onClick={() => setShowReview(!showReview)}
        className="w-full px-3 py-2 mb-2 text-sm text-gray-400 hover:text-gray-50 transition-colors flex items-center justify-between border border-gray-700 rounded-lg hover:border-gray-600"
      >
        <span className="flex items-center gap-2">
          <Eye className="w-4 h-4" />
          Review
        </span>
        {showReview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {showReview && (
        <div className="space-y-2 mb-4">
          {reviewFilters.map((filter) => {
            const Icon = filter.icon;
            const isActive = primaryFilter === filter.key;

            return (
              <button
                key={filter.key}
                onClick={() => handlePrimaryChange(filter.key)}
                className={`
                  w-full px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-all
                  ${isActive
                    ? `${getBadgeColor(filter.key)} text-white shadow-lg transform scale-[1.02]`
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-gray-50'
                  }
                `}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium flex-1 text-left">{filter.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Refine Button */}
      <button
        onClick={() => setShowRefine(!showRefine)}
        className="w-full px-3 py-2 text-sm text-gray-400 hover:text-gray-50 transition-colors flex items-center justify-between border border-gray-700 rounded-lg hover:border-gray-600"
      >
        <span>Refine</span>
        {showRefine ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Secondary Filters — Hidden by Default */}
      {showRefine && (
        <div className="mt-3 p-3 bg-gray-800/50 rounded-lg space-y-3 border border-gray-700">
          {/* Entity Type Filter */}
          <div>
            <div className="text-xs font-semibold text-gray-400 mb-2">Show</div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/50 p-1.5 rounded transition-colors">
                <input
                  type="radio"
                  checked={!entityType}
                  onChange={() => handleEntityTypeChange(null)}
                  className="w-4 h-4 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                />
                <span className="text-sm text-gray-300">All</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/50 p-1.5 rounded transition-colors">
                <input
                  type="radio"
                  checked={entityType === 'leads_only'}
                  onChange={() => handleEntityTypeChange('leads_only')}
                  className="w-4 h-4 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                />
                <span className="text-sm text-gray-300">Leads Only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/50 p-1.5 rounded transition-colors">
                <input
                  type="radio"
                  checked={entityType === 'customers_only'}
                  onChange={() => handleEntityTypeChange('customers_only')}
                  className="w-4 h-4 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                />
                <span className="text-sm text-gray-300">Customers Only</span>
              </label>
            </div>
          </div>

        </div>
      )}

      {/* Filter explanation text */}
      <div className="mt-3 text-xs text-gray-500 italic">
        {primaryFilter === 'ai_picks' && !entityType && (
          "Showing top opportunities ranked by AI"
        )}
        {primaryFilter === 'planned_today' && (
          "Showing entities you planned to visit today"
        )}
        {primaryFilter === 'urgent' && (
          "Showing at-risk customers and overdue visits"
        )}
        {primaryFilter === 'new_opportunities' && (
          "Showing new leads to explore"
        )}
        {primaryFilter === 'all_entities' && (
          "Showing all stores and customers"
        )}
        {primaryFilter === 'done' && (
          "Showing recently completed visits"
        )}
        {primaryFilter === 'skipped' && (
          "Showing skipped and cancelled visits"
        )}
        {entityType === 'leads_only' && " · Leads only"}
        {entityType === 'customers_only' && " · Customers only"}
      </div>
    </div>
  );
};

export default SimpleFilters;
